import { utils } from "washday-sdk";
import { clearHistoricalPromotionRoleForPricing } from "@washday/utilities";
import { ensureLineIds, resolveLineId, stabilizeBuyAndGetLineIds } from "./lineIdentity";
import { maybeRecomputePieceCount } from "./pieceCount";

type RecalculateOrderProductsInput = {
  discountCodeObj: any;
  products: any[];
  express: boolean;
  storeDiscounts: any;
  customerInfo: any;
  previousBuyAndGetProducts?: any[];
  orderType?: 'standard' | 'return';
  returnItemsChargeMode?: 'no_charge' | 'charge';
};

// Mirrors the backend return snapshot boundary plus identity/option fields.
// Commercial discount fields are intentionally left on the SDK result.
const IMMUTABLE_RETURN_LINE_FIELDS = [
  '_id',
  'lineId',
  'orderProductLineID',
  'returnSourceOrderLineId',
  'productId',
  'storeProductId',
  'name',
  'price',
  'expressPrice',
  'extraAmount',
  'manualUnitPrice',
  'quantity',
  'qty',
  'pieceCount',
  'pieces',
  'attributes',
  'properties',
  'joinedProperties',
  'selectedOption',
  'selectedOptionId',
  'type',
  'minimumBillableQuantity',
  'taxExemptOne',
  'taxExemptTwo',
  'taxExemptThree',
  'isBuyAndGetProduct',
  'isFreeItem',
  'overlayText',
  'sku',
  'isActive',
  'showInApp',
  'owner',
  'store',
  'image',
  'order',
  'productSupplies',
  'invoice_description',
  'invoice_product_key',
  'invoice_product_unit_key',
  'invoice_product_unit_name',
  'invoiceModuleSettings',
  'section',
  'isOutsourced',
  'outsourcingPartner',
  'barcode'
] as const;

const restoreImmutableReturnLineFields = (
  recalculatedLine: Record<string, unknown>,
  snapshot: Record<string, unknown>
): Record<string, unknown> => {
  const restoredLine: Record<string, unknown> = { ...recalculatedLine };

  IMMUTABLE_RETURN_LINE_FIELDS.forEach((field) => {
    if (Object.prototype.hasOwnProperty.call(snapshot, field)) {
      restoredLine[field] = snapshot[field];
    }
  });

  return restoredLine;
};

export const recalculateOrderProductsWithLineIds = ({
  discountCodeObj,
  products,
  express,
  storeDiscounts,
  customerInfo,
  previousBuyAndGetProducts = [],
  orderType,
  returnItemsChargeMode
}: RecalculateOrderProductsInput) => {
  // Existing API lines can retain legacy `qty`, while Web POS edits `quantity`.
  // Keep both synchronized before passing the line to SDK calculations.
  const productsWithLineIds = ensureLineIds(
    products.map((line) => {
      const quantity = line.quantity ?? line.qty;
      return { ...line, quantity, qty: quantity };
    })
  );
  const provenanceProductsByLineId = new Map(
    productsWithLineIds
      .filter((line) => Boolean(line.returnSourceOrderLineId))
      .map((line) => [resolveLineId(line), line])
  );
  const preservedProductsByLineId = new Map(
    productsWithLineIds.map((line) => [
      resolveLineId(line),
      {
        pieceCount: line.pieceCount ?? null,
        __pieceCountTouched: line.__pieceCountTouched ?? false
      }
    ])
  );
  const previousBuyAndGetProductsWithLineIds = ensureLineIds(previousBuyAndGetProducts);
  const provenanceBuyAndGetProducts = previousBuyAndGetProductsWithLineIds.filter(
    (line) => Boolean(line.returnSourceOrderLineId)
  );
  const provenanceBuyAndGetProductsByLineId = new Map(
    provenanceBuyAndGetProducts.map((line) => [resolveLineId(line), line])
  );
  const promotionProvenancePricingLines = provenanceBuyAndGetProducts.map(
    clearHistoricalPromotionRoleForPricing
  );
  const previousGeneratedBuyAndGetProducts = previousBuyAndGetProductsWithLineIds.filter(
    (line) => !line.returnSourceOrderLineId
  );
  const preservedBuyAndGetByLineId = new Map(
    previousGeneratedBuyAndGetProducts.map((line) => [
      resolveLineId(line),
      {
        pieceCount: line.pieceCount ?? null,
        __pieceCountTouched: line.__pieceCountTouched ?? false
      }
    ])
  );

  const { newOrderProds, buyAndGetProds } = utils.applyDiscountToProducts(
    discountCodeObj,
    [...productsWithLineIds, ...promotionProvenancePricingLines],
    express,
    storeDiscounts,
    {
      customer: customerInfo,
      discount: Number(customerInfo?.discount ?? 0)
    },
    orderType || returnItemsChargeMode
      ? { orderType, returnItemsChargeMode }
      : {}
  );

  const returnedProvenanceLineIds = new Set<string>();
  const returnedPromotionProvenanceLineIds = new Set<string>();
  const nextProducts: any[] = [];
  const nextPromotionProvenanceLines: any[] = [];
  ensureLineIds(newOrderProds).forEach((line) => {
    const lineId = resolveLineId(line);
    const promotionProvenanceSnapshot = provenanceBuyAndGetProductsByLineId.get(lineId);
    if (promotionProvenanceSnapshot) {
      returnedPromotionProvenanceLineIds.add(lineId);
      nextPromotionProvenanceLines.push({
        ...restoreImmutableReturnLineFields(line, promotionProvenanceSnapshot),
        isBuyAndGetProduct: false,
        isFreeItem: false
      });
      return;
    }
    const immutableSnapshot = provenanceProductsByLineId.get(lineId);
    if (immutableSnapshot) {
      returnedProvenanceLineIds.add(lineId);
      nextProducts.push(restoreImmutableReturnLineFields(line, immutableSnapshot));
      return;
    }
    const preserved = preservedProductsByLineId.get(resolveLineId(line));
    nextProducts.push(maybeRecomputePieceCount({
      ...line,
      pieceCount: preserved?.pieceCount ?? line.pieceCount ?? null,
      __pieceCountTouched: preserved?.__pieceCountTouched ?? line.__pieceCountTouched ?? false
    }));
  });
  provenanceProductsByLineId.forEach((line, lineId) => {
    if (!returnedProvenanceLineIds.has(lineId)) nextProducts.push(line);
  });
  provenanceBuyAndGetProductsByLineId.forEach((line, lineId) => {
    if (!returnedPromotionProvenanceLineIds.has(lineId)) {
      nextPromotionProvenanceLines.push(clearHistoricalPromotionRoleForPricing(line));
    }
  });
  const nextBuyAndGetProducts = stabilizeBuyAndGetLineIds(
    previousGeneratedBuyAndGetProducts,
    buyAndGetProds
  );
  const nextBuyAndGetProductsWithPieceCount = nextBuyAndGetProducts.map((line) => {
    const preserved = preservedBuyAndGetByLineId.get(resolveLineId(line));
    return maybeRecomputePieceCount({
      ...line,
      pieceCount: preserved?.pieceCount ?? line.pieceCount ?? null,
      __pieceCountTouched: preserved?.__pieceCountTouched ?? line.__pieceCountTouched ?? false
    });
  });

  return {
    newOrderProds: nextProducts,
    buyAndGetProds: [
      ...nextPromotionProvenanceLines,
      ...nextBuyAndGetProductsWithPieceCount
    ]
  };
};
